/**
 * views/products/useCoursePurchase.js
 *
 * The education enrolment / purchase flow, extracted so more than one course page can share a
 * single implementation.
 *
 * This is the exact sequence the Default course detail page performs (CourseInfo.jsx):
 *   1. not signed in → remember where we were and send the candidate to /sign-in
 *   2. schedule required but none chosen → ask for a schedule, do nothing else
 *   3. otherwise → build the cart line item, `addToShoppingCart`, mirror it into CartContext,
 *      build the order request, `createOrder` (or `updateOrder` when an order already exists),
 *      persist `orderDetails` + `persistedOrder`, dispatch `setOrder`, then navigate to
 *      /enrollment-details where payment continues.
 *
 * Payload shapes, localStorage keys, call order and error mapping are all preserved verbatim
 * from CourseInfo so the two pages cannot diverge on a money path.
 */

import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createOrder, updateOrder } from '../../api/action/cart';
import { setOrder } from '../../api/reducers/orderReducer';
import { useCart } from '../../context/CartContext';
import { useAuthSession } from '../../auth/useAuthSession';
import { storePendingNavigation } from '../../utils/pendingNavigation.js';
import { addToShoppingCart } from './ProductToCart';
import { saveScheduledProduct } from '../../api/action/schedule';

/** Maps backend order failures onto candidate-facing copy. Verbatim from CourseInfo. */
export const getOrderErrorMessage = (error) => {
  const rawMessage = error?.message
    || error?.response?.data?.message
    || error?.fallbackMessage
    || (typeof error === 'string' ? error : '');

  if (rawMessage.includes('Duplicate purchase is not allowed')) {
    return 'You are already enrolled in this schedule. Please choose a different schedule or Course.';
  }
  if (rawMessage.includes('already purchased')) {
    return 'This course has already been purchased for your account.';
  }
  return rawMessage || "We couldn't process your enrollment right now. Please try again.";
};

const EMPTY_ALERT = { open: false, message: '', severity: 'info' };

const isInterviewProduct = (product) => {
  const subtype = product?.productCategorySubType
    || product?.product?.productCategorySubType
    || product?.scheduledProduct?.productCategorySubType;
  if (typeof subtype === 'string') return subtype.toUpperCase() === 'INTERVIEW';
  return ['name', 'code', 'typeCode'].some((key) => String(subtype?.[key] || '').toUpperCase() === 'INTERVIEW');
};

const toLocalDateTime = (date, time = '00:00:00') => {
  const normalizedTime = String(time || '00:00:00').slice(0, 8);
  return `${date}T${normalizedTime.length === 5 ? `${normalizedTime}:00` : normalizedTime}`;
};

/**
 * @returns {{
 *   purchase: (product: object, options?: { isScheduleRequired?: boolean, selectedSchedule?: object|string }) => Promise<void>,
 *   isProcessing: boolean,
 *   alert: { open: boolean, message: string, severity: string },
 *   dismissAlert: () => void,
 *   isUserLoggedIn: boolean
 * }}
 */
export const useCoursePurchase = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToCart, updateCart } = useCart() || {};
  const authData = useSelector((state) => state?.auth?.data);
  const { isAuthenticated, isLoggedIn } = useAuthSession();
  // Memoized so the `|| {}` fallback does not produce a new object on every render, which would
  // invalidate the callbacks below each time.
  const userDetail = useMemo(() => authData || {}, [authData]);
  const companyState = useSelector((state) => state.companyReducer?.company);

  const [isProcessing, setIsProcessing] = useState(false);
  const [alert, setAlert] = useState(EMPTY_ALERT);

  const isUserLoggedIn = useMemo(() => Boolean(
    (isAuthenticated || isLoggedIn)
    && (userDetail?.primaryId || userDetail?.account?.primaryId || localStorage.getItem('token'))
  ), [isAuthenticated, isLoggedIn, userDetail]);

  const dismissAlert = useCallback(() => setAlert((current) => ({ ...current, open: false })), []);

  const updateCartWithProduct = useCallback(async (productDetails, isScheduled, selectedCourse) => {
    const shoppingCartId = userDetail?.account?.shoppingCart?.primaryId;

    const newItem = {
      quantity: 1,
      totalPrice: productDetails.price?.priceAmt || 0,
    };

    if (isScheduled && selectedCourse) {
      newItem.scheduledProduct = {
        primaryId: selectedCourse.primaryId,
        product: {
          ...productDetails,
          price: {
            priceAmt: productDetails.price?.priceAmt || 0,
            priceDurationType: productDetails.price?.priceDurationType,
          },
          period: selectedCourse.period,
          effectiveStartDate: selectedCourse.period?.effectiveStartDate,
          effectiveEndDate: selectedCourse.period?.effectiveEndDate,
        },
      };
    } else {
      newItem.product = {
        ...productDetails,
        primaryId: productDetails.primaryId,
        price: {
          priceAmt: productDetails.price?.priceAmt || 0,
          priceDurationType: productDetails.price?.priceDurationType,
        },
      };
    }

    const updatedProductList = [newItem];
    const totalAmount = updatedProductList.reduce((sum, item) => sum + item.totalPrice, 0);

    const cartPayload = {
      primaryId: shoppingCartId,
      accountId: userDetail.account?.primaryId,
      createdBy: userDetail.firstName,
      modifiedBy: userDetail.firstName,
      totalAmount,
      totalPrice: totalAmount,
      productList: updatedProductList,
      company: companyState,
    };

    const cartResponse = await addToShoppingCart(cartPayload, navigate);
    if (cartResponse) {
      addToCart?.(cartPayload);
      updateCart?.(updatedProductList);
    }
    return cartResponse;
  }, [addToCart, companyState, navigate, updateCart, userDetail]);

  const createOrderFromProduct = useCallback(async (productDetails, isScheduled, selectedCourse) => {
    const lineItem = {
      quantity: 1,
      totalPrice: productDetails.price?.priceAmt || 0,
      price: { priceAmt: productDetails.price?.priceAmt || 0 },
    };

    if (isScheduled && selectedCourse) {
      lineItem.scheduledProduct = {
        product: {
          name: productDetails.name,
          code: productDetails.code,
          primaryId: productDetails.primaryId,
          type: productDetails.type,
          productCategorySubType: productDetails.productCategorySubType,
          price: {
            priceAmt: productDetails.price?.priceAmt || 0,
            priceDurationType: productDetails.price?.priceDurationType,
          },
        },
        schedule: { primaryId: selectedCourse.primaryId },
        primaryId: selectedCourse.primaryId,
      };
    } else {
      lineItem.product = {
        name: productDetails.name,
        code: productDetails.code,
        primaryId: productDetails.primaryId,
        type: productDetails.type,
        productCategorySubType: productDetails.productCategorySubType,
        price: {
          priceAmt: productDetails.price?.priceAmt || 0,
          priceDurationType: productDetails.price?.priceDurationType,
        },
      };
    }

    const enrollmentCompany = (isScheduled && selectedCourse?.company) || productDetails.company;

    const orderRequest = {
      createdBy: `${userDetail.firstName} ${userDetail.lastName}`,
      modifiedBy: `${userDetail.firstName} ${userDetail.lastName}`,
      accountId: userDetail.account?.primaryId,
      totalLineItems: 1,
      totalAmount: productDetails.price?.priceAmt || 0,
      subtotalAmount: productDetails.price?.priceAmt || 0,
      shippingAndHandlingAmount: 0,
      tax: 0,
      deliveryType: productDetails?.deliveryType || 'BRICK_MORTAR',
      lineItemList: [lineItem],
      company: {
        primaryId: enrollmentCompany?.primaryId || companyState?.primaryId,
        domain: { type: enrollmentCompany?.domain?.type || companyState?.domain?.type },
      },
    };

    const existingOrder = JSON.parse(localStorage.getItem('orderDetails'));
    let orderResponse;

    if (!existingOrder) {
      localStorage.removeItem('persistedOrder');
      orderResponse = await createOrder(orderRequest);
    } else {
      localStorage.removeItem('persistedOrder');
      orderResponse = await updateOrder({ ...existingOrder, ...orderRequest });
    }

    localStorage.setItem('orderDetails', JSON.stringify(orderResponse));
    dispatch(setOrder(orderResponse));
    localStorage.setItem('persistedOrder', JSON.stringify({
      ...orderResponse,
      timestamp: new Date().getTime(),
    }));

    return orderResponse;
  }, [companyState, dispatch, userDetail]);

  const createInterviewScheduledProduct = useCallback(async (productDetails, slot) => {
    const interviewerId = slot?.assignedProductId || slot?.suggestedProduct?.primaryId;
    if (!interviewerId || !slot?.date || !slot?.startTime || !slot?.endTime) {
      throw new Error('Please select an interviewer and available time slot.');
    }
    const timeZone = companyState?.operatingLocation?.zoneId
      || Intl.DateTimeFormat().resolvedOptions().timeZone
      || 'UTC';
    const durationMinutes = slot.intervalInMinutes
      || Math.max(1, (new Date(toLocalDateTime(slot.date, slot.endTime))
        - new Date(toLocalDateTime(slot.date, slot.startTime))) / 60000);

    return saveScheduledProduct({
      period: {
        effectiveDate: {
          localStartDate: toLocalDateTime(slot.date, slot.startTime),
          localEndDate: toLocalDateTime(slot.date, slot.endTime),
          timeZone,
          considerTimePortion: true,
        },
        durationType: 'HOUR',
        duration: durationMinutes / 60,
      },
      owners: [{ primaryId: interviewerId }],
      clients: userDetail?.primaryId ? [{ primaryId: userDetail.primaryId }] : [],
      product: { primaryId: productDetails.primaryId, productCategorySubType: 'INTERVIEW' },
      company: { primaryId: companyState?.primaryId, domain: { type: 'EDUCATION' } },
      createdBy: `${userDetail.firstName || ''} ${userDetail.lastName || ''}`.trim() || userDetail.email || 'user',
      modifiedBy: `${userDetail.firstName || ''} ${userDetail.lastName || ''}`.trim() || userDetail.email || 'user',
      hasSchedule: true,
      status: 'ACTIVE',
      hasChild: false,
      associatedScheduledProducts: [],
      name: `${productDetails.name} - Interview - ${slot.date}`,
    });
  }, [companyState, userDetail]);

  const purchase = useCallback(async (productDetails, options = {}) => {
    const { isScheduleRequired = false, selectedSchedule = null, interviewSlot = null } = options;

    if (!productDetails) return;

    if (!isUserLoggedIn) {
      storePendingNavigation();
      setAlert({ open: true, severity: 'warning', message: 'Please sign in to continue with enrollment.' });
      setTimeout(() => navigate('/sign-in'), 1200);
      return;
    }

    if (isInterviewProduct(productDetails) && !interviewSlot) {
      setAlert({ open: true, severity: 'warning', message: 'Please select an interviewer and time slot.' });
      return;
    }

    if (!isInterviewProduct(productDetails) && isScheduleRequired && !selectedSchedule) {
      setAlert({ open: true, severity: 'warning', message: 'Please pick your schedule.' });
      return;
    }

    try {
      setIsProcessing(true);
      const interviewSchedule = isInterviewProduct(productDetails)
        ? await createInterviewScheduledProduct(productDetails, interviewSlot)
        : null;
      if (isInterviewProduct(productDetails) && !interviewSchedule?.primaryId) {
        throw new Error('This slot is no longer available. Please select another time.');
      }
      const isScheduled = Boolean(interviewSchedule || (isScheduleRequired && selectedSchedule));
      const selectedCourse = typeof selectedSchedule === 'string'
        ? JSON.parse(selectedSchedule)
        : (interviewSchedule || selectedSchedule);
      await updateCartWithProduct(productDetails, isScheduled, selectedCourse);
      const orderResponse = await createOrderFromProduct(productDetails, isScheduled, selectedCourse);
      if (orderResponse) navigate('/enrollment-details');
    } catch (error) {
      setAlert({ open: true, severity: 'error', message: getOrderErrorMessage(error) });
      setIsProcessing(false);
    }
  }, [createInterviewScheduledProduct, createOrderFromProduct, isUserLoggedIn, navigate, updateCartWithProduct]);

  return { purchase, isProcessing, alert, dismissAlert, isUserLoggedIn };
};

export default useCoursePurchase;
